import { List, Map } from 'immutable'
import { ColumnDefinition } from '../components/grid/GridColumns'
import { AppStateType } from '../utils/appStateReduxStore'
import { createImmutableEqualSelector } from './createImmutableEqualSelector'

const gridsSelector = (state: AppStateType) => state.getIn(['grid'])

export const gridByIdSelector = createImmutableEqualSelector(
  gridsSelector,
  (state: AppStateType, gridKey: string) => gridKey,
  (grids, gridKey) => (grids ? grids.get(gridKey, Map()) : Map())
)

export const selectedIdsByGridKeySelector = createImmutableEqualSelector(
  gridByIdSelector,
  (grids) => grids?.get('selectedIds') || List()
)

export const sortedColumnKeyByGridKeySelector: (
  state: AppStateType,
  gridKey: string,
  defaultSortingColumn: string
) => string = createImmutableEqualSelector(
  gridByIdSelector,
  (state: AppStateType, gridKey: string, defaultSortingColumn: string) => defaultSortingColumn,
  (grids: Map<string, any>, defaultSortingColumn: string) => grids?.get('sortedColumnKey') || defaultSortingColumn
)

export const isSortingOrderDescendingSelector: (
  state: AppStateType,
  gridKey: string,
  defaultSortingColumn: string,
  isDefaultSortingOrderDescending: boolean
) => boolean = createImmutableEqualSelector(
  gridByIdSelector,
  (state: AppStateType, gridKey: string, defaultSortingColumn: string, isDefaultSortingOrderDescending: boolean) =>
    isDefaultSortingOrderDescending,
  (grids: Map<string, any>, isDefaultSortingOrderDescending: boolean) =>
    grids?.get('sortDescending') || isDefaultSortingOrderDescending
)

export const filterColumnsSelector: <T>(
  columns: List<ColumnDefinition<T>>,
  hiddenColumns: List<keyof T>
) => List<ColumnDefinition<T>> = createImmutableEqualSelector(
  (columns: List<ColumnDefinition<any>>) => columns,
  (columns: List<ColumnDefinition<any>>, hiddenColumns: List<unknown>) => hiddenColumns,
  (columns: List<ColumnDefinition<any>>, hiddenColumns: List<unknown>): List<ColumnDefinition<any>> =>
    columns.filter((column) => !hiddenColumns.includes(column.dataIndex) || column.hidden)
)

export const moveColumnInOrder = (order: List<string>, dragDataIndex: string, dropDataIndex: string): List<string> => {
  const from = order.indexOf(dragDataIndex)
  const to = order.indexOf(dropDataIndex)
  if (from === -1 || to === -1 || from === to) {
    return order
  }
  const without = order.delete(from)
  return without.insert(without.indexOf(dropDataIndex), dragDataIndex)
}

export const orderColumnsSelector: <T>(
  columns: List<ColumnDefinition<T>>,
  order: List<string>
) => List<ColumnDefinition<T>> = createImmutableEqualSelector(
  (columns: List<ColumnDefinition<any>>) => columns,
  (_columns: List<ColumnDefinition<any>>, order: List<string>) => order,
  (columns: List<ColumnDefinition<any>>, order: List<string>): List<ColumnDefinition<any>> => {
    if (order.isEmpty()) {
      return columns
    }
    const rank = (column: ColumnDefinition<any>) => {
      const i = order.indexOf(String(column.dataIndex))
      return i === -1 ? order.size : i
    }
    return columns
      .map((column, originalIndex) => ({ column, originalIndex }))
      .sort((a, b) => rank(a.column) - rank(b.column) || a.originalIndex - b.originalIndex)
      .map((it) => it.column)
      .toList()
  }
)
